import { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { useApiContext } from 'src/compass/site/providers';
import { MAXIMUM_LIMIT_MESSAGE } from 'src/compass/shared/schemas/getStringSchema';

// Direct path, not the application barrel: this file is inside it, and importing it from itself is
// a cycle (see CasPendingEmailsPill in CasContextProviders).
import { SendChannelType } from './composeMode';

/*
  Pictures in a message Body, on the way out and on the way back in.

  While the sender works, the compose editor holds each picture as a data: URI - it has to show it,
  and normalizeImage has already sized it to send. Nothing that leaves the browser carries one:

    * on Send, or a template save, every data: picture becomes cid:img-<sha256 of its bytes>, and
      its bytes go once in the request's `images` - however many bodies in a batch share the same
      screenshot. The server keeps each distinct picture once (msg.Images) and attaches it to every
      email as the inline attachment that cid names.
    * a saved SCE template comes back holding those references, so before it reaches an editor
      they are swapped back for data: URIs, fetched from Msg/Images/{hash}.

  Pictures are remembered by hash for the session, so re-applying a template - or applying one just
  saved - fetches nothing.
*/

export interface IInlineImage {
  // Lowercase hex SHA-256 of the bytes: the <hash> in cid:img-<hash>.
  hash: string;
  // The bytes, base64.
  content: string;
}

// Every data: picture as the editor serializes it (ImageNode.exportDOM, always double-quoted).
const DATA_IMAGE = /(<img\b[^>]*?\bsrc=")(data:image\/[a-z0-9.+-]+;base64,([A-Za-z0-9+/=]+))(")/gi;

const REFERENCE = /(<img\b[^>]*?\bsrc=")cid:img-([0-9a-f]{64})(")/gi;

// cid:img- and 64 hex digits: what a picture of any size costs a body once it is sent.
const REFERENCE_LENGTH = 72;

// The server's cap on one body (SCEReportBatchSendEmailRequestBoValidator), and getMaxStringSchema's.
const MAX_BODY_LENGTH = 15000;

/*
  What one message's pictures may add up to - InlineImages.MaxEmailImageBytes / MaxTextImageBytes
  on the server, which refuses the same thing. Each is well inside its carrier's own limit (Graph's
  4 MB per email, RingCentral's 1.5 MB per MMS) because an SCE message's table images share it.
*/
export const MAX_EMAIL_IMAGE_BYTES = 2 * 1024 * 1024;
export const MAX_TEXT_IMAGE_BYTES = 1024 * 1024;

// Hash -> data: URI, for every picture this session has sent, saved or fetched.
const known = new Map<string, string>();

const toHex = (buffer: ArrayBuffer) =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// A data: URI's payload, and its size decoded - base64 is 4 characters for every 3 bytes.
const payloadOf = (dataUri: string) => dataUri.slice(dataUri.indexOf(',') + 1);

const decodedBytes = (base64: string) => {
  const padding = (base64.match(/=+$/)?.[0] ?? '').length;
  return Math.floor((base64.length * 3) / 4) - padding;
};

async function hashOf(dataUri: string): Promise<string> {
  const hash = toHex(await crypto.subtle.digest('SHA-256', base64ToBytes(payloadOf(dataUri))));
  known.set(hash, dataUri);
  return hash;
}

/*
  Swap every data: picture in these bodies for its reference, and gather each distinct picture once.

  The bodies come back in the order given - pass a batch's default body and every row's own in one
  call and they share one list. A body that is null or undefined comes back as it went in.
*/
export async function toTransport<T extends string | null | undefined>(
  bodies: T[]
): Promise<{ bodies: T[]; images: IInlineImage[] }> {
  const hashes = new Map<string, string>();
  const images = new Map<string, IInlineImage>();

  const uris = Array.from(
    new Set(
      bodies.flatMap((body) =>
        body ? Array.from(body.matchAll(DATA_IMAGE)).map((match) => match[2]) : []
      )
    )
  );

  await Promise.all(
    uris.map(async (uri) => {
      const hash = await hashOf(uri);
      hashes.set(uri, hash);
      images.set(hash, { hash, content: payloadOf(uri) });
    })
  );

  return {
    bodies: bodies.map((body) =>
      body
        ? (body.replace(
            DATA_IMAGE,
            (_, open: string, uri: string, _payload: string, close: string) =>
              `${open}cid:img-${hashes.get(uri)}${close}`
          ) as T)
        : body
    ),
    images: Array.from(images.values()),
  };
}

/*
  A text's pictures, as the request's `images`. A text's body is plain words with nowhere to name
  them, so the list itself is what gets attached.
*/
export async function toTransportImages(sources: string[]): Promise<IInlineImage[]> {
  const distinct = Array.from(new Set(sources.filter((src) => src.startsWith('data:'))));

  const images = await Promise.all(
    distinct.map(async (uri) => ({ hash: await hashOf(uri), content: payloadOf(uri) }))
  );

  // Two different data: URIs can only share a hash if they are the same picture; keep it once.
  return images.filter((image, i) => images.findIndex((other) => other.hash === image.hash) === i);
}

export const hasImageReferences = (html?: string | null) => !!html && /cid:img-[0-9a-f]{64}/i.test(html);

export interface IResolvedImages {
  // The body, each reference that could be fetched swapped for its picture.
  html: string;
  // Hashes the server has no picture for (404). Trying again will not bring them back.
  missing: string[];
  // Hashes that could not be fetched this time - a network drop, a 5xx, a sign-in that could not
  // be refreshed. Worth trying again.
  failed: string[];
}

/*
  A saved body's references swapped back for pictures the editor can show, and the ones that could
  not be. Those are left in the body as they are, and the editor's importer drops an unresolved
  cid: - so a caller that loads the html anyway loses the picture without a word. Check missing and
  failed first.
*/
export async function resolveInlineImages(html: string, api: any): Promise<IResolvedImages> {
  const hashes = Array.from(
    new Set(Array.from(html.matchAll(REFERENCE)).map((match) => match[2].toLowerCase()))
  ).filter((hash) => !known.has(hash));

  const missing: string[] = [];
  const failed: string[] = [];

  await Promise.all(
    hashes.map(async (hash) => {
      try {
        const { data } = await api({ method: 'get', url: `Msg/Images/${hash}` });
        if (data?.content) known.set(hash, `data:${data.contentType};base64,${data.content}`);
        else missing.push(hash);
      } catch (error: any) {
        (error?.response?.status === 404 ? missing : failed).push(hash);
      }
    })
  );

  return {
    html: html.replace(REFERENCE, (whole, open: string, hash: string, close: string) => {
      const uri = known.get(hash.toLowerCase());
      return uri ? `${open}${uri}${close}` : whole;
    }),
    missing,
    failed,
  };
}

// resolveInlineImages, bound to this user's API client.
export function useInlineImageResolver() {
  const { api } = useApiContext();

  return useCallback((html: string) => resolveInlineImages(html, api), [api]);
}

// The body with these pictures taken out - for going on without ones the server no longer has.
const withoutReferences = (html: string, hashes: string[]) =>
  html.replace(/<img\b[^>]*?\bsrc="cid:img-([0-9a-f]{64})"[^>]*>/gi, (whole, hash: string) =>
    hashes.includes(hash.toLowerCase()) ? '' : whole
  );

export type ResolvedBodyType =
  | { state: 'loading' }
  | { state: 'ready'; html: string }
  // A picture could not be fetched this time.
  | { state: 'failed'; count: number; retry: VoidFunction }
  // The server no longer has a picture; continueWithout goes on with the body minus it.
  | { state: 'missing'; count: number; continueWithout: VoidFunction };

/*
  A saved body with its pictures resolved, for a form that reads its body once, at mount.

  It is never handed a body that lost a picture by accident: while one could not be fetched it
  stays 'failed' until retry brings it back, and one the server no longer has is left out only
  once the user chooses to go on without it. A body without references is handed straight back,
  so a form opened on one renders without a loading step.
*/
export function useResolvedBody(html?: string | null): ResolvedBodyType {
  const resolve = useInlineImageResolver();
  const [attempt, setAttempt] = useState(0);
  const [resolved, setResolved] = useState<
    { source: string; attempt: number } & IResolvedImages
  >();
  const [withoutMissing, setWithoutMissing] = useState(false);

  const source = html ?? '';
  const needsResolving = hasImageReferences(source);

  useEffect(() => {
    if (!needsResolving) return undefined;

    let cancelled = false;
    resolve(source).then((result) => {
      if (!cancelled) setResolved({ source, attempt, ...result });
    });

    return () => {
      cancelled = true;
    };
  }, [source, needsResolving, resolve, attempt]);

  if (!needsResolving) return { state: 'ready', html: source };

  if (resolved?.source !== source || resolved.attempt !== attempt) return { state: 'loading' };

  if (resolved.failed.length)
    return {
      state: 'failed',
      count: resolved.failed.length,
      retry: () => setAttempt((n) => n + 1),
    };

  if (resolved.missing.length && !withoutMissing)
    return {
      state: 'missing',
      count: resolved.missing.length,
      continueWithout: () => setWithoutMissing(true),
    };

  return { state: 'ready', html: withoutReferences(resolved.html, resolved.missing) };
}

// A body's data: pictures, each once, in the order they appear - for listing what a text will carry.
export const imageSources = (html?: string | null) =>
  Array.from(new Set(Array.from((html ?? '').matchAll(DATA_IMAGE)).map((match) => match[2])));

// The decoded size of each distinct data: picture in these bodies, added up.
export function imageBytes(...bodies: (string | null | undefined)[]): number {
  const uris = new Set(
    bodies.flatMap((body) =>
      body ? Array.from(body.matchAll(DATA_IMAGE)).map((match) => match[2]) : []
    )
  );

  return Array.from(uris).reduce((total, uri) => total + decodedBytes(payloadOf(uri)), 0);
}

export const sourceBytes = (sources: string[]) =>
  Array.from(new Set(sources)).reduce((total, uri) => total + decodedBytes(payloadOf(uri)), 0);

const megabytes = (bytes: number) => `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;

/*
  Why one message's pictures are more than its channel can carry, or null when they fit. Worded to
  follow a message's name - "Acme Farms: 2.4 MB of images, and an email can carry at most 2 MB" -
  the way the server words the same refusal.
*/
export function imageBudgetProblem(bytes: number, channel: SendChannelType): string | null {
  const limit = channel === 'sms' ? MAX_TEXT_IMAGE_BYTES : MAX_EMAIL_IMAGE_BYTES;

  if (bytes <= limit) return null;

  return `${megabytes(bytes)} of images, and ${
    channel === 'sms' ? 'a text' : 'an email'
  } can carry at most ${megabytes(limit)}`;
}

/*
  How many attachments one text may carry - SmsService.MaxMmsAttachments on the server, which
  refuses the same thing. A text attaches a JPEG per SCE table and then each of its pictures, and
  how many there are is known before Send even though the tables' bytes are not.
*/
export const MAX_MMS_ATTACHMENTS = 10;

// Every SCE table a rich body names: a <dataobject> holding its JSON (see prepareHtmlForEmail).
const DATA_OBJECT = /<dataobject\b[^>]*>([\s\S]*?)<\/dataobject>/gi;

// The tables a rich body names, counted as the server parses them - an empty one is not a table.
export const dataObjectCount = (html?: string | null) =>
  Array.from((html ?? '').matchAll(DATA_OBJECT)).filter((match) => match[1].trim()).length;

/*
  Why a text carrying this many pictures and tables is more than one MMS can carry, or null when
  it fits. Worded to follow a message's name, as imageBudgetProblem is.
*/
export function attachmentCountProblem(count: number): string | null {
  if (count <= MAX_MMS_ATTACHMENTS) return null;

  return `${count} pictures and tables, and a text can carry at most ${MAX_MMS_ATTACHMENTS}`;
}

// The same for one text on its own, in the server's words (SmsService.TooManyAttachments).
export function tooManyAttachmentsMessage(count: number): string | null {
  if (count <= MAX_MMS_ATTACHMENTS) return null;

  return `This text has ${count} pictures and tables; a text can carry at most ${MAX_MMS_ATTACHMENTS}. Remove some images, or send it as an email.`;
}

/*
  How long a body counts as against the 15,000-character limit: as it will be sent, where every
  picture is a 72-character reference whatever its size. Counting the data: URI instead would put a
  single screenshot over the limit many times.
*/
export const bodyLength = (html?: string | null) =>
  Array.from((html ?? '').matchAll(DATA_IMAGE)).reduce(
    (length, match) => length - match[2].length + REFERENCE_LENGTH,
    (html ?? '').length
  );

// getMaxStringSchema for a rich Body - the same limit, counted by bodyLength.
export const getMaxBodySchema = () =>
  Yup.string()
    .nullable()
    .test('max-body-length', MAXIMUM_LIMIT_MESSAGE, (value) => bodyLength(value) <= MAX_BODY_LENGTH);
